import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { referralService } from '../../services/ReferralsServices';

const ReferralList = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    fetchReferrals();
  }, [currentPage, perPage, search]);

  const fetchReferrals = async () => {
    try {
      setLoading(true);
      const response = await referralService.listReferrals({
        page: currentPage,
        limit: perPage,
        search: search
      });
      
      if (response?.data?.success) {
        setReferrals(response.data.data);
        setTotalRows(response.data.pagination.totalCount);
      } else {
        setError('Failed to load referral list.');
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch referral list:', error);
      setError('Failed to load referral list.');
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const columns = [
    {
      name: 'Referrer Name',
      selector: row => row.user_id?.fullName || 'N/A',
      sortable: true,
      minWidth: '150px'
    },
    {
      name: 'Email',
      selector: row => row.user_id?.email || 'N/A',
      sortable: true,
      minWidth: '200px'
    },
    {
      name: 'Referral Code',
      selector: row => row.referral_code,
      sortable: true,
      minWidth: '120px'
    },
    {
      name: 'Total Referrals',
      selector: row => row.total_referrals,
      sortable: true,
      center: true,
      minWidth: '80px'
    },
    {
      name: 'Active Referrals',
      selector: row => row.active_referrals,
      sortable: true,
      center: true,
      minWidth: '80px'
    },
    {
      name: 'Multiplier',
      selector: row => `${row.current_multiplier}%`,
      sortable: true,
      center: true,
      minWidth: '80px'
    },
    {
      name: 'Total Earnings',
      selector: row => formatCurrency(row.total_earnings),
      sortable: true,
      right: true,
      minWidth: '120px'
    },
    {
      name: 'Date Created',
      selector: row => formatDate(row.createdAt),
      sortable: true,
      minWidth: '120px'
    },
    {
      name: 'Status',
      cell: row =>
        row.status === 'active' ? (
          <span className="badge bg-success">Active</span>
        ) : (
          <span className="badge bg-secondary">Inactive</span>
        ),
      sortable: true,
      center: true,
      minWidth: '80px'
    },
    {
      name: 'Action',
      cell: row => (
        <Link 
          to={`/admin/referral-details/${row.user_id?._id}`} 
          className="btn btn-outline-secondary btn-sm"
          title="View Details"
        >
          <i className="fa fa-eye"></i>
        </Link>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: '80px'
    }
  ];

  const customStyles = {
    headCells: {
      style: {
        fontWeight: 'bold',
        fontSize: '14px',
      },
    },
    cells: {
      style: {
        fontSize: '13px',
      },
    },
  };

  return (
    <div className="body d-flex py-3">
      <div className="container-xxl">
        <div className="row align-items-center">
          <div className="border-0 mb-4">
            <div className="card-header py-3 no-bg bg-transparent d-flex align-items-center px-0 justify-content-between border-bottom flex-wrap">
              <h3 className="fw-bold mb-0">Referral List</h3>
              <div className="d-flex align-items-center">
                <span className="badge bg-primary me-2">
                  Total: {totalRows}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="row align-item-center">
          <div className="col-md-12">
            <div className="card mb-3">
              <div className="card-header py-3 bg-transparent border-bottom-0">
                <h6 className="mb-0 fw-bold">Referrals</h6>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, email, or code"
                      value={search}
                      onChange={handleSearch}
                    />
                  </div>
                </div>
                
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
                
                <DataTable
                  columns={columns}
                  data={referrals}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                  highlightOnHover
                  progressPending={loading}
                  noDataComponent={
                    <div className="text-center py-4">
                      <i className="fa fa-search fa-2x text-muted mb-2"></i>
                      <p className="text-muted">No referrals found</p>
                    </div>
                  }
                  customStyles={customStyles}
                  responsive
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralList;